<template>
  <span
    ref="targetRef"
    class="popover"
  >
    <slot />

    <Portal
      v-if="open"
      :disabled="!usePortal"
      to="fullscreen"
    >
      <div
        v-if="emptySubstrate"
        class="popover__empty-wrapper"
      />
      <div
        ref="contentRef"
        :style="floatingStyles"
        class="popover__content"
        role="dialog"
      >
        <slot name="content" />
      </div>
    </Portal>
  </span>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import type { OffsetOptions, Placement } from '@floating-ui/vue'
import { offset as setOffset } from '@floating-ui/vue'
import { autoUpdate, flip, useFloating, shift } from '@floating-ui/vue'
import { Portal } from 'portal-vue'
import type { PopOverMiddleware } from '@/uiKit/popOver.utils'
import { popOverMiddlewares } from '@/uiKit/popOver.utils'

const props = withDefaults(
  defineProps<{
    open?: boolean
    offset?: OffsetOptions
    /**
     * List of strings representing middleware names.
     * Middlewares are custom behaviours accepted by floating-ui to customise the floating menu
     * behaviour.
     * https://floating-ui.com/docs/middleware
     */
    middleware?: PopOverMiddleware[] | undefined
    placement?: Placement
    /**
     * We should avoid using portal unless necessary. 99% of popovers can be rendered alongside the
     * target element, with some edge cases needing a different placement or an extra style.
     */
    usePortal?: boolean
    /**
     * Add an empty substrate to the popover to prevent the click events outside the popover.
     */
    emptySubstrate?: boolean
    /**
     * Whether the popover should follow the target when it moves.
     * When false, the popover will stay in its initial position.
     */
    followTarget?: boolean
  }>(),
  {
    open: false,
    placement: 'top',
    offset: 5,
    middleware: undefined,
    usePortal: false,
    followTarget: true,
  },
)

const targetRef = ref<HTMLElement | null>(null)
const contentRef = ref<HTMLElement | null>(null)

const middleware = [flip(), setOffset(props.offset), shift()]
if (props.middleware) {
  props.middleware.forEach((middlewareName) => {
    const customMiddleware = popOverMiddlewares[middlewareName]
    if (customMiddleware) {
      middleware.push(customMiddleware)
    }
  })
}

const { floatingStyles } = useFloating(targetRef, contentRef, {
  placement: props.placement,
  strategy: 'fixed',
  whileElementsMounted: props.followTarget ? autoUpdate : undefined,
  middleware,
})
</script>

<style lang="scss" scoped>
.popover__content {
  z-index: var(--workview-center-main-area-popup-menu);
}

.popover__empty-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: var(--workview-center-main-area-popup-menu);
  background-color: transparent;
}
</style>
